import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<p class=debrief__status>DEBRIEFING…`),
  _tmpl$2 = /*#__PURE__*/_$template(`<p class="debrief__status debrief__status--error">The baked assets could not be loaded.`),
  _tmpl$3 = /*#__PURE__*/_$template(`<div class=debrief role=dialog aria-label="Mission debriefing"data-testid=debrief><canvas class=debrief__canvas aria-label="Mission debriefing">`);
/**
 * MISSION DEBRIEFING over a finished sortie, drawn as one of the shell's own
 * 320×200 screens.
 *
 * The same relationship to `/career`'s debriefing that
 * `PreferencesOverlay.tsx` has to `/settings`: one measured screen, one
 * painter, mounted over a flying route instead of beside a menu. Before this,
 * `/fly` ended on `DebriefOverlay.tsx` — a modern HTML card — while `/career`
 * ended on the real screen, so the same sortie was reported two different ways
 * depending on which door the player came through.
 *
 * ## It swallows the keyboard, for `PreferencesOverlay`'s reason
 *
 * `docs/engineering/lessons.md` (2026-08-25): chrome over the flight view that
 * the input layer does not know about lets input through to the viewport, whose
 * primary control is the trigger. The listener below is on the **capture**
 * phase at `window`, so `keyboard.ts`'s bubble-phase listener is never reached
 * and no key that works this screen can also fire the guns of an aeroplane that
 * has already landed. `keyup` is deliberately not swallowed — a key held when
 * the sortie ended still has to release.
 *
 * Mounted only once a flight is decided, so a sortie nobody finishes never pays
 * to decode the shell's fonts, palette and sprite sheet.
 */

import { createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { createDebriefSurface } from "../shell/debrief-surface.ts";
import { createScreenRuntime } from "../shell/screen-runtime.ts";
export function DebriefScreen(props) {
  const surface = createDebriefSurface({
    debrief: () => props.debrief(),
    nation: () => props.nation ?? "allied",
    onContinue: () => props.onContinue()
  });
  // No title theme: this screen opens over a sortie whose engine has only just
  // stopped (`screen-runtime.ts`'s `ScreenRuntimeOptions.music`).
  const runtime = createScreenRuntime(surface, () => {}, {
    music: false
  });
  const [status, setStatus] = createSignal(runtime.status());
  runtime.subscribe(setStatus);
  let canvas;
  function onKeyDown(event) {
    // A browser shortcut is still the browser's, exactly as `keyboard.ts`
    // decides for the aeroplane.
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    event.stopPropagation();
    event.preventDefault();
    runtime.keyDown(event);
  }
  onMount(() => {
    runtime.mount(canvas);
    const observer = new ResizeObserver(() => runtime.refresh());
    if (canvas) observer.observe(canvas);
    window.addEventListener("keydown", onKeyDown, {
      capture: true
    });
    onCleanup(() => {
      observer.disconnect();
      window.removeEventListener("keydown", onKeyDown, {
        capture: true
      });
      runtime.dispose();
    });
  });

  // The report can arrive after the mount — the route decides a flight is over
  // and the signal fills in on the same tick — so a repaint is forced on it.
  createEffect(() => {
    if (!props.debrief()) return;
    surface.reopen();
    runtime.refresh();
  });
  return (() => {
    var _el$ = _tmpl$3(),
      _el$2 = _el$.firstChild;
    _el$2.$$pointermove = event => runtime.pointerMove(event);
    _el$2.$$pointerdown = event => runtime.pointerDown(event);
    _$use(element => canvas = element, _el$2);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return status() === "loading";
      },
      get children() {
        return _tmpl$();
      }
    }), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return status() === "failed";
      },
      get children() {
        return _tmpl$2();
      }
    }), null);
    return _el$;
  })();
}
_$delegateEvents(["pointerdown", "pointermove"]);